"use client";

import { useCallback, useMemo, useRef } from "react";

import { usePreferencesQuery } from "./query";
import { usePreferenceMutations } from "./mutations";
import type {
  PreferenceCategory,
  PreferencesBundle,
  UIPreferences,
  NotificationPreferences,
  WorkflowPreferences,
  AIPreferences,
} from "./types";
export type * from "./types";

const EMPTY_PREFERENCES: Partial<PreferencesBundle> = {};

export function useUserPreferences() {
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const query = usePreferencesQuery();

  const mutations = usePreferenceMutations({
    api: query.api,
    organizationId: query.organizationId,
    refetchPreferences: query.refetch,
    downloadRef,
  });

  const updatePreferences = useCallback(
    async (
      category: PreferenceCategory,
      prefs:
        | Partial<UIPreferences>
        | Partial<NotificationPreferences>
        | Partial<WorkflowPreferences>
        | Partial<AIPreferences>
        | Record<string, unknown>,
    ) => {
      await mutations.updateMutation.mutateAsync({ category, prefs });
    },
    [mutations.updateMutation],
  );

  const resetToDefaults = useCallback(
    async (category?: PreferenceCategory) =>
      mutations.resetToDefaults(category),
    [mutations],
  );

  const handleExport = useCallback(() => mutations.handleExport(), [mutations]);

  const importPreferences = useCallback(
    (payload: string) => mutations.importPreferences(payload),
    [mutations],
  );

  const error = query.error
    ? query.error instanceof Error
      ? query.error.message
      : "Failed to fetch preferences"
    : null;

  const preferences = useMemo(
    () => query.data ?? EMPTY_PREFERENCES,
    [query.data],
  );

  return {
    preferences,
    isLoading: query.isLoading,
    error,
    updatePreferences,
    resetToDefaults,
    handleExport,
    importPreferences,
    refreshPreferences: () => query.refetch(),
    downloadRef,
  };
}

export function useUIPreferences() {
  const { preferences, updatePreferences } = useUserPreferences();
  return {
    ui: (preferences as Partial<PreferencesBundle>).ui,
    updateUI: (prefs: Partial<UIPreferences>) => updatePreferences("ui", prefs),
  };
}

export function useNotificationPreferences() {
  const { preferences, updatePreferences } = useUserPreferences();
  return {
    notifications: (preferences as Partial<PreferencesBundle>).notifications,
    updateNotifications: (prefs: Partial<NotificationPreferences>) =>
      updatePreferences("notifications", prefs),
  };
}

export function useWorkflowPreferences() {
  const { preferences, updatePreferences } = useUserPreferences();
  return {
    workflow: (preferences as Partial<PreferencesBundle>).workflow,
    updateWorkflow: (prefs: Partial<WorkflowPreferences>) =>
      updatePreferences("workflow", prefs),
  };
}

export function useAIPreferences() {
  const { preferences, updatePreferences } = useUserPreferences();
  return {
    ai: (preferences as Partial<PreferencesBundle>).ai,
    updateAI: (prefs: Partial<AIPreferences>) => updatePreferences("ai", prefs),
  };
}
